/**
 * Maps API auth payloads to a single client user shape.
 * Login returns `id` + `role`; GET /api/auth/me returns `userId` + `roleName` and no display_name.
 */
export interface SessionUser {
    id: string;
    email: string;
    display_name: string;
    role: string;
    /** Effective capability keys (from GET /api/auth/me). Empty on the
     *  login payload, which doesn't include them; auth.tsx refreshes via /me. */
    permissions: string[];
    /** Owning org id when present. */
    orgId: string | null;
    /** When set, this session is an admin impersonating another user. */
    actingAs: { actorUserId: string } | null;
    /** True when this user is a ride-along field helper (Helper Mode):
     *  sees the paired tech's jobs, enters data, never signs/submits. */
    is_field_helper: boolean;
    /** Compensation type: 'hourly' (office day-clock), 'point' (field tech),
     *  'salary'. Drives the office timeclock punch + hard clock-in gate. Only
     *  present from GET /api/auth/me (null on the login payload). */
    pay_type: string | null;
}

/** Accepts login or /me JSON user objects (partial / loose). */
export function normalizeSessionUser(body: Record<string, unknown> | null | undefined): SessionUser | null {
    if (!body || typeof body !== "object") return null;
    const idRaw = body.userId ?? body.id;
    const id = typeof idRaw === "string" ? idRaw : typeof idRaw === "number" ? String(idRaw) : "";
    if (!id) return null;

    const email = typeof body.email === "string" ? body.email : "";
    const roleRaw = body.role ?? body.roleName ?? "";
    const role = typeof roleRaw === "string" ? roleRaw : "";

    let display_name = typeof body.display_name === "string" ? body.display_name.trim() : "";
    if (!display_name) {
        const fn = typeof body.firstName === "string" ? body.firstName.trim() : "";
        const ln = typeof body.lastName === "string" ? body.lastName.trim() : "";
        display_name = [fn, ln].filter(Boolean).join(" ");
    }
    if (!display_name && email) {
        display_name = email.split("@")[0] || "User";
    }
    if (!display_name) display_name = "User";

    const permissions = Array.isArray(body.permissions)
        ? body.permissions.filter((p): p is string => typeof p === "string")
        : [];
    const orgId = typeof body.orgId === "string" ? body.orgId : null;
    const actingAsRaw = body.actingAs;
    const actingAs =
        actingAsRaw &&
        typeof actingAsRaw === "object" &&
        typeof (actingAsRaw as Record<string, unknown>).actorUserId === "string"
            ? { actorUserId: (actingAsRaw as Record<string, unknown>).actorUserId as string }
            : null;

    const is_field_helper = body.isFieldHelper === true || body.is_field_helper === true;
    const payRaw = body.payType ?? body.pay_type;
    const pay_type = typeof payRaw === "string" ? payRaw : null;

    return { id, email, display_name, role, permissions, orgId, actingAs, is_field_helper, pay_type };
}
